import { useCallback, useRef } from 'react';
import Editor, { OnMount, OnChange } from '@monaco-editor/react';
import type { editor } from 'monaco-editor';
import { cn } from '../../lib/utils';

export interface CodeEditorProps {
  /** Current value as a string */
  value: string;
  /** Callback when value changes */
  onChange?: (value: string) => void;
  /** Editor language */
  language?: 'json' | 'javascript';
  /** Height of the editor (default: 200px) */
  height?: string | number;
  /** Whether the editor is disabled/read-only */
  disabled?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Monaco theme */
  theme?: 'vs-light' | 'vs-dark';
}

/**
 * Generalized code editor component using Monaco Editor.
 *
 * Supports JSON and JavaScript languages. JSON mode includes
 * diagnostics/validation; JS mode uses basic syntax highlighting.
 *
 * @example
 * ```tsx
 * <CodeEditor
 *   value={code}
 *   onChange={setCode}
 *   language="javascript"
 *   height={300}
 * />
 * ```
 */
export function CodeEditor({
  value,
  onChange,
  language = 'json',
  height = 200,
  disabled = false,
  className,
  theme = 'vs-light',
}: CodeEditorProps) {
  const editorRef = useRef<editor.IStandaloneCodeEditor | null>(null);

  const handleEditorMount: OnMount = useCallback((editorInstance, monaco) => {
    editorRef.current = editorInstance;

    // Configure JSON-specific diagnostics
    if (language === 'json') {
      monaco.languages.json.jsonDefaults.setDiagnosticsOptions({
        validate: true,
        schemas: [],
        allowComments: false,
        schemaValidation: 'error',
      });
    }
  }, [language]);

  const handleChange: OnChange = useCallback((newValue) => {
    onChange?.(newValue || '');
  }, [onChange]);

  return (
    <div className={cn('relative', className)}>
      <div
        className={cn(
          'rounded-md border overflow-hidden transition-colors',
          'border-input focus-within:ring-ring',
          disabled && 'opacity-50 cursor-not-allowed',
          'focus-within:ring-2 focus-within:ring-offset-2'
        )}
      >
        <Editor
          height={height}
          defaultLanguage={language}
          value={value}
          onChange={handleChange}
          onMount={handleEditorMount}
          options={{
            minimap: { enabled: false },
            scrollBeyondLastLine: false,
            fontSize: 13,
            lineNumbers: 'on',
            lineNumbersMinChars: 3,
            folding: true,
            bracketPairColorization: { enabled: true },
            automaticLayout: true,
            tabSize: 2,
            wordWrap: 'on',
            readOnly: disabled,
            domReadOnly: disabled,
            padding: { top: 8, bottom: 8 },
            renderLineHighlight: disabled ? 'none' : 'line',
            smoothScrolling: true,
            formatOnPaste: !disabled,
            formatOnType: !disabled,
            quickSuggestions: language === 'javascript',
            suggestOnTriggerCharacters: language === 'javascript',
            scrollbar: {
              vertical: 'auto',
              horizontal: 'auto',
              verticalScrollbarSize: 10,
              horizontalScrollbarSize: 10,
            },
          }}
          theme={theme}
          loading={
            <div className="flex items-center justify-center h-full text-sm text-muted-foreground">
              Loading editor...
            </div>
          }
        />
      </div>
    </div>
  );
}

export default CodeEditor;
